/**
 * The moon, drawn from its real geometry: the lit limb is a semicircle and the terminator
 * is a half-ellipse whose width depends on how lit the disc is.
 */

export interface MoonProps {
  /** 0..1 fraction of the synodic month since new moon. */
  age: number;
  hemisphere?: "northern" | "southern";
  size?: number;
  /** Accessible description, e.g. "Waxing crescent, 18% illuminated". */
  label: string;
}

/** SVG path for the lit part of a moon of radius `r` centred at (r, r). Empty at new moon. */
export function moonPath(
  age: number,
  r: number,
  hemisphere: "northern" | "southern" = "northern",
): string {
  const a = ((age % 1) + 1) % 1;
  const lit = (1 - Math.cos(2 * Math.PI * a)) / 2;
  if (lit < 0.005) return "";
  if (lit > 0.995) return `M ${r} 0 A ${r} ${r} 0 1 1 ${r} ${2 * r} A ${r} ${r} 0 1 1 ${r} 0 Z`;
  const waxing = a < 0.5;
  const litRight = waxing === (hemisphere === "northern");
  const rx = Math.abs(1 - 2 * lit) * r;
  const crescent = lit < 0.5;
  const limbSweep = litRight ? 1 : 0;
  const terminatorSweep = litRight ? (crescent ? 0 : 1) : crescent ? 1 : 0;
  const f = (n: number) => Number(n.toFixed(2));
  return `M ${r} 0 A ${r} ${r} 0 0 ${limbSweep} ${r} ${2 * r} A ${f(rx)} ${r} 0 0 ${terminatorSweep} ${r} 0 Z`;
}

export function Moon({ age, hemisphere = "northern", size = 160, label }: MoonProps) {
  const r = 50;
  return (
    <svg
      className="hx-moon"
      width={size}
      height={size}
      viewBox="-2 -2 104 104"
      role="img"
      aria-label={label}
    >
      <circle className="hx-moon__dark" cx={r} cy={r} r={r} />
      <path className="hx-moon__lit" d={moonPath(age, r, hemisphere)} />
      <circle className="hx-moon__rim" cx={r} cy={r} r={r} />
    </svg>
  );
}
